import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { ModalMessageInfo } from '@app/core/components/modal';
import { confirmNegative } from '@app/core/components/modal/confirmation-modals';
import { LeavePageActionTypes, resetFormDirtyStatus } from '@app/core/leave-page-handler/actions/leave-page';
import {
  GoToNextPageUrlOnConfirm,
  LeavePageCanceled,
  LeavePageInternalActionTypes,
  PreventLeaveEditingFail,
  PreventLeavePageFail,
  resetLeavePageActionResult,
  setLeavePageActionResult
} from '@app/core/leave-page-handler/actions/leave-page-internal';
import { LeavePageActionResult } from '@app/core/leave-page-handler/models/leave-page-action-result';
import { LeavePageService } from '@app/core/leave-page-handler/service/leave-page.service';
import * as fromPcs from '@app/pcs/reducer';
import { mapPayload, takeOne } from '@app/util';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, select, Store } from '@ngrx/store';
import * as R from 'ramda';
import { Observable, of } from 'rxjs';
import { catchError, switchMap, tap, withLatestFrom } from 'rxjs/operators';

const openUnsavedChangesModal = (calledFromGuard, confirmActions: Action[] = []) => {
  const messageInfo: ModalMessageInfo = {
    titleText: 'general.message.cancel.title',
    message: 'general.message.cancel.changes',
    buttonText: 'general.message.cancel.changes.cancel',
    cancelBtnText: 'general.message.cancel.changes.goback'
  };

  const leavePageConfirmActions = R.concat(
    [setLeavePageActionResult(LeavePageActionResult.Confirm), resetFormDirtyStatus()],
    calledFromGuard ? [new GoToNextPageUrlOnConfirm()] : []
  );
  return of(confirmNegative(
    messageInfo,
    R.concat(confirmActions, leavePageConfirmActions),
    calledFromGuard ? [resetLeavePageActionResult()] : [setLeavePageActionResult(LeavePageActionResult.Cancel)]
  ));
};

@Injectable()
export class LeavePageEffects {

  preventLeavePage$ = createEffect(() => this.actions$.pipe(
    ofType(LeavePageInternalActionTypes.PreventLeavePage),
    mapPayload(),
    withLatestFrom(this.store.pipe(select(fromPcs.getLeavePageFormDirty))),
    switchMap(([{confirmActions}, formDirty]: [any, boolean]) =>
      this.preventUnsavedChanges(true, formDirty, confirmActions)
    ),
    catchError(err => of(new PreventLeavePageFail(err)))
  ));

  preventLeaveEditing$ = createEffect(() => this.actions$.pipe(
    ofType(LeavePageInternalActionTypes.PreventLeaveEditing),
    mapPayload(),
    withLatestFrom(this.store.pipe(select(fromPcs.getLeavePageFormDirty))),
    switchMap(([{confirmActions}, formDirty]: any) =>
      this.preventUnsavedChanges(false, formDirty, confirmActions)
    ),
    catchError(err => of(new PreventLeaveEditingFail(err)))
  ));

  goToNextPageUrlOnConfirm$ = createEffect(() => this.actions$.pipe(
    ofType(LeavePageInternalActionTypes.GoToNextPageUrlOnConfirm),
    withLatestFrom(this.store.pipe(select(fromPcs.getLeavePageNextUrl))),
    tap(([_, nextPageUrl]: any) => {
      if (nextPageUrl) {
        this.router.navigateByUrl(nextPageUrl);
      }
    })
  ), {dispatch: false});

  leavePageWithoutNavigation$ = createEffect(() => this.actions$.pipe(
    ofType(
      LeavePageActionTypes.SelectionChanged,
      LeavePageActionTypes.LeavePageConfirmation
    ),
    mapPayload(),
    switchMap(({actions}: any): Observable<any> => this.leavePageService.check().pipe(
      takeOne(),
      switchMap((confirmed) => confirmed ? actions : [new LeavePageCanceled()]),
      catchError(error => of(new PreventLeavePageFail(error)))
    ))
  ));

  preventUnsavedChanges = (calledFromGuard: boolean, formDirty: boolean, confirmActions: Action[]) =>
    formDirty
      ? openUnsavedChangesModal(calledFromGuard, confirmActions)
      : calledFromGuard ? R.append(new GoToNextPageUrlOnConfirm(), confirmActions) : confirmActions;

  constructor(
    private actions$: Actions,
    private router: Router,
    private store: Store<fromPcs.State>,
    private leavePageService: LeavePageService
  ) {
  }

}
